import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositePlan.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { RefreshCw, AlertCircle } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositePlanBadge from '../components/MicrositePlanBadge/MicrositePlanBadge'
import MicrositePlanExpiryCountdown from '../components/MicrositePlanExpiryCountdown/MicrositePlanExpiryCountdown'
import MicrositePlanBanner from '../components/MicrositePlanBanner/MicrositePlanBanner'
import MicrositeUsageBar from '../components/MicrositeUsageBar/MicrositeUsageBar'
import MicrositeFeatureFlagGrid from '../components/MicrositeFeatureFlagGrid/MicrositeFeatureFlagGrid'

/**
 * MicrositePlan
 * Read-only plan overview — limits, current usage, feature flags,
 * and expiry. Plan changes happen on Lokaci's side (sales/admin),
 * so there's no Save button here; this is purely informational for
 * the salon owner to understand what their plan includes.
 */
const MicrositePlan = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [plan, setPlan] = useState(null)

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadPlan = useCallback(async () => {
        setLoading(true)
        setError(false)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            setPlan(res.response?.plan || {})
        } else {
            setError(true)
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadPlan()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const expiryDays = useMemo(() => {
        if (!plan?.expiresOn) return null
        const diff = new Date(plan.expiresOn.replace(' ', 'T')).getTime() - Date.now()
        return Math.ceil(diff / 86400000)
    }, [plan])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading plan details…</span>
            </div>
        )
    }

    if (error) {
        return (
            <div className={styles.errorWrap}>
                <AlertCircle size={18} />
                <span>Could not load plan details. Please refresh.</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <MicrositePageHeader
                title="Plan & limits"
                subtitle="Your current plan, usage, and what's included."
            />

            <MicrositePlanBanner daysRemaining={expiryDays} />

            <section className={styles.fieldGroup}>
                <div className={styles.planHeaderRow}>
                    <div>
                        <div className={styles.planNameRow}>
                            <MicrositePlanBadge planName={plan?.planName || 'Free'} />
                        </div>
                        <MicrositePlanExpiryCountdown expiresOn={plan?.expiresOn} />
                    </div>
                </div>
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Usage</h2>
                <p className={styles.groupDesc}>How much of your plan's allowance you're currently using.</p>

                <MicrositeUsageBar label="Storage" used={plan?.usedStorageGb ?? 0} max={plan?.maxStorageGb ?? 1} unit="GB" />
                <MicrositeUsageBar label="API calls today" used={plan?.usedApiCallsToday ?? 0} max={plan?.maxApiCallsPerDay ?? 1000} unit="" />
                <MicrositeUsageBar label="Emails today" used={plan?.usedEmailsToday ?? 0} max={plan?.maxEmailsPerDay ?? 100} unit="" />
                <MicrositeUsageBar label="Team members" used={plan?.usedUsers ?? 0} max={plan?.maxUsers ?? 1} unit="" />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Features included</h2>
                <p className={styles.groupDesc}>What's available to you on the {plan?.planName || 'current'} plan.</p>

                <MicrositeFeatureFlagGrid features={plan?.features || {}} />
            </section>

            {plan?.overageChargesEnabled && (
                <div className={styles.overageNote}>
                    Overage charges are enabled — exceeding your plan limits may incur additional charges.
                </div>
            )}
        </div>
    )
}

export default MicrositePlan